import { useState, FormEvent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { makeApiCall } from "src/api/apiRequests";
import API_HEADERS from "src/api/apiConfig";
import API_PATHS from "src/api/apiPaths";
import ValidationMessage from "src/helpers/ValidationMessage";
import FLASH_MESSAGES from "src/helpers/FlashMessages";

interface EditAllergicGroupProps {
  onShowModal: boolean;
  setShowModal: (visible: boolean) => void;
  allergicGroupId: number;
  setModified: (visible: boolean) => void;
}

const EditAllergicGroup: React.FC<EditAllergicGroupProps> = ({
  onShowModal,
  setShowModal,
  allergicGroupId,
  setModified,
}) => {
  const { t } = useTranslation();

  const [name, setName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  const [loadingButton, setLoadingButton] = useState<boolean>(false);
  const [validations, setValidations] = useState<Record<string, string>>({});

  const onClose = () => {
    setShowModal(false);
  };

  const getAllergicGroup = async (): Promise<void> => {
    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.allergicGroupShow(allergicGroupId),
        "GET",
        API_HEADERS.authenticated
      );
      setName(response.allergicGroup.name);
      setLoading(false);
    } catch (error: any) {
      FLASH_MESSAGES.errorMsg(error.response.data.error);
      onClose();
    }
  };

  useEffect(() => {
    getAllergicGroup();
  }, []);

  const editAllergicGroup = async (
    e: FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();
    setLoadingButton(true);

    const formData = {
      name: name,
    };

    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.allergicGroupUpdate(allergicGroupId),
        "POST",
        API_HEADERS.authenticated,
        formData
      );

      FLASH_MESSAGES.successMsg(response.success);

      setModified(true);

      onClose();

      setLoadingButton(false);
    } catch (error: any) {
      if (error.response.status === 422) {
        setValidations(error.response.data);
      } else {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
        onClose();
      }
      setLoadingButton(false);
    }
  };

  return (
    <Modal
      isOpen={onShowModal && !loading}
      toggle={() => onClose()}
      size="md"
      centered
    >
      <ModalHeader
        className="text-dark blue font-size-15"
        toggle={() => onClose()}
        tag="span"
      >
        <i className="icon allergy-icon" />
        {t("meal_allergies_edit_group_title")}
      </ModalHeader>
      <form onSubmit={(e) => editAllergicGroup(e)}>
        <ModalBody>
          <div className="modal-note">
            {t("meal_allergies_edit_group_desc")}
          </div>
          <div className="form-block">
            <label className="required">
              {t("meal_allergies_edit_group_label")}
            </label>
            <input
              type="text"
              name="name"
              placeholder={t("meal_allergies_edit_group_placeholder")}
              value={name}
              className={
                "form-input" + (validations.name ? " input-error" : "")
              }
              autoComplete="off"
              onChange={(e) => setName(e.target.value)}
            />
            {validations.name && (
              <ValidationMessage message={validations.name[0]} />
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            className="button-white"
            type="button"
            onClick={() => onClose()}
          >
            {t("meal_allergies_edit_group_cancel_button")}
          </button>

          {loadingButton ? (
            <button type="button" className="button-blue has-icon">
              <i className="spinner-icon" />
              {t("meal_allergies_edit_group_editing_button")}
            </button>
          ) : (
            <button type="submit" className="button-blue has-icon">
              <i className="icon add-icon" />
              {t("meal_allergies_edit_group_edit_button")}
            </button>
          )}
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default EditAllergicGroup;
